import jsPDF from 'jspdf';
import type { PublisherConfig, SelfServiceSubmissionRow } from '../types/selfService';
import { formatDate, formatDateTime } from '../components/self-service/shared/dateFormat';
import { bucketByView as bucketTrend, pickDefaultView as pickTrendView, viewAdverbial } from '../components/self-service/trendBuckets';
import { niceAxisRange, fmtAxis } from '../components/self-service/chartAxis';
import { NAME_TO_ABBR } from '../components/self-service/shared/usMapProjection';
import { computeZipMapFrame, mercatorY } from '../components/self-service/shared/zipMapFrame';
import { heatRamp } from '../components/self-service/reportTheme';
import { DMA_REGIONS } from '../data/dmaList';
import { CTVBUYER_WORDMARK_PNG, CTVBUYER_WORDMARK_W, CTVBUYER_WORDMARK_H } from './ctvbuyerWordmark';

// Full state name from its 2-letter code (inverse of NAME_TO_ABBR) for the
// geographic table; falls back to the raw code.
const ABBR_TO_NAME: Record<string, string> = Object.fromEntries(
  Object.entries(NAME_TO_ABBR).map(([name, abbr]) => [abbr, name]),
);
const DMA_NAME_BY_CODE: Record<string, string> = Object.fromEntries(
  DMA_REGIONS.map((d) => [d.code, d.name]),
);

// Page geometry (mm). A4 portrait, identical to the order receipt so the two
// exports look like siblings.
const PAGE_W = 210;
const PAGE_H = 297;
const MARGIN_X = 18;
const MARGIN_TOP = 20;
const MARGIN_BOTTOM = 22;
const CONTENT_W = PAGE_W - MARGIN_X * 2;
const PAGE_USABLE_BOTTOM = PAGE_H - MARGIN_BOTTOM;
// Vertical space a section title occupies before its content (the `y += 4` every
// section advances after drawing its title).
const SECTION_TITLE_GAP = 4;

const COLOR_TEXT = [15, 23, 42] as const;       // slate-900
const COLOR_MUTED = [100, 116, 139] as const;   // slate-500
const COLOR_LINE = [226, 232, 240] as const;    // slate-200
const COLOR_BAR = [14, 165, 233] as const;      // sky-500 (consistent neutral; we don't have hex→rgb util)

interface DailyPlay {
  date: string;
  plays: number;
}

interface MonthlyRow {
  monthKey: string;
  label: string;
  days: number;
  plays: number;
  dailyAvg: number;
}

interface GeoDatum {
  // Raw key: state 2-letter code / DMA Nielsen code / ZIP. Used for labeling.
  code: string;
  // Optional resolved display name (e.g. census DMA name); preferred over the
  // code→name lookup when present.
  name?: string | null;
  impressions: number;
  completes: number;
}

export interface DeliveryReportPdfData {
  row: SelfServiceSubmissionRow;
  config: PublisherConfig;
  daily: DailyPlay[];
  monthly: MonthlyRow[];
  totalDelivered: number;
  dailyAvg: number;
  goalPct: number | null;
  // CTV-445: overall video completion rate (0–100), shown as a summary stat.
  completionRate?: number | null;
  // CTV-445: daypart — the named broadcast windows already shifted to the
  // campaign's market timezone by the caller, plus the short zone label (e.g. "CT").
  daypart?: {
    windows: Array<{ label: string; hours: string; impressions: number; completes: number }>;
    timezoneLabel: string;
  };
  // CTV-445: geographic delivery. The three independent rollups (sorted desc by
  // the caller) plus the delivered ZIP boundaries (+ impressions) used to draw a
  // vector ZIP choropleth framed on the main delivery area.
  geo?: {
    byState: GeoDatum[];
    byDma: GeoDatum[];
    byZip: GeoDatum[];
    zipBoundaries?: Array<{
      zcta: string;
      latitude: number;
      longitude: number;
      boundary: GeoJSON.Geometry | null;
      impressions: number;
    }> | null;
    // Mapbox static-image URL for the same frame, drawn under the ZIP polygons
    // as a basemap (streets/water for context). Skipped if absent / unloadable.
    zipBasemapUrl?: string | null;
  };
  // CTV-445: per-creative delivery — only passed for multi-creative campaigns
  // (mirrors the on-page section, which hides for a single creative).
  creatives?: Array<{ name: string; impressions: number; completes: number }>;
  // CTV-445: theme the bars + heatmap to the same brand accent the on-page
  // report uses (advertiser's primary color; the green default when absent).
  accentColor?: string | null;
  // CTV-445: advertiser + publisher logos (data URLs, fetched server-side)
  // embedded side-by-side in the PDF header row. Each is skipped gracefully if
  // it can't be loaded.
  logoUrl?: string | null;
  publisherLogoUrl?: string | null;
  // CTV-445: when the report data was last synced (preformatted UTC string, e.g.
  // "Jun 8, 2026, 12:04 PM UTC"). Shown under the "Generated" header line.
  dataUpdatedLabel?: string | null;
  // CTV-462: when present, this PDF is scoped to a single flight (one month
  // of a monthly campaign) rather than the lifetime campaign. The label is
  // injected into the header subtitle; date range narrows to the scoped
  // window. The caller is responsible for passing pre-filtered `daily`,
  // `monthly`, and the stat numbers computed against the scope.
  scope?: {
    label: string;          // e.g. "May 2026"
    startDate: string;      // YYYY-MM-DD (scoped window start)
    endDate: string;        // YYYY-MM-DD (scoped window end)
  };
}

export async function exportDeliveryReportToPdf(
  data: DeliveryReportPdfData,
  filename: string,
): Promise<void> {
  const { row, config, daily, monthly, totalDelivered, dailyAvg, goalPct, scope, completionRate, daypart, geo, creatives, accentColor, logoUrl, publisherLogoUrl, dataUpdatedLabel } = data;

  // Load both logos up front (async) so they can sit in the header row. Each
  // resolves null on any failure (CORS, bad format) → header omits just that one.
  const [advLogo, pubLogo] = await Promise.all([
    logoUrl ? loadLogoImage(logoUrl) : Promise.resolve(null),
    publisherLogoUrl ? loadLogoImage(publisherLogoUrl) : Promise.resolve(null),
  ]);

  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
    compress: true,
  });

  // Theme bars + heatmap to the brand accent like the on-page report. Bars use
  // the solid accent; the choropleth uses the same heatRamp(accent) stops the
  // on-page maps do (default green when there's no usable brand color).
  const accentValid = !!accentColor && /^#[0-9a-f]{6}$/i.test(accentColor);
  const barColor: readonly [number, number, number] = accentValid
    ? hexToRgbLocal(accentColor as string)
    : COLOR_BAR;
  const ramp = heatRamp(accentColor ?? undefined);
  const rampRgb = { low: hexToRgbLocal(ramp.low), mid: hexToRgbLocal(ramp.mid), high: hexToRgbLocal(ramp.high) };

  const campaignName = row.campaign?.campaignName || row.business_info?.businessName || '—';
  // CTV-462: dates and subtitle narrow when a scope is supplied.
  const startDate = scope?.startDate ?? row.campaign?.startDate ?? '';
  const endDate = scope?.endDate ?? row.campaign?.endDate ?? '';
  const subtitle = scope ? `Delivery Report · ${scope.label}` : 'Delivery Report';

  let y = MARGIN_TOP;

  // --- Header: publisher + subtitle + generated date (left), advertiser logo
  // (right). The logo sits in the title row, matching the on-page header.
  pdf.setTextColor(...COLOR_TEXT);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(16);
  pdf.text(config.name, MARGIN_X, y);

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(10);
  pdf.setTextColor(...COLOR_MUTED);
  pdf.text(subtitle, MARGIN_X, y + 5);

  pdf.setFontSize(9);
  let metaY = y + 9.5;
  pdf.text(`Generated ${formatDateTime(new Date().toISOString())}`, MARGIN_X, metaY);
  // Data-freshness line (when the report data was last synced), under "Generated".
  if (dataUpdatedLabel) {
    metaY += 4;
    pdf.text(`Data updated ${dataUpdatedLabel}`, MARGIN_X, metaY);
  }

  let headerBottom = metaY;
  // Advertiser + publisher logos, right-aligned as a group (advertiser rightmost,
  // publisher to its left), vertically centered on a shared line. When both are
  // present, a small "×" sits between them to read as a collaboration.
  const headerLogos = [advLogo, pubLogo].filter((l): l is NonNullable<typeof l> => !!l);
  if (headerLogos.length > 0) {
    const MAX_H = 14;
    const MAX_W = 44;
    const LOGO_GAP = 6;
    const X_GAP = 12; // wider gap between the two logos to hold the ×
    const both = headerLogos.length === 2;
    const centerY = y - 5 + MAX_H / 2;
    let rightEdge = PAGE_W - MARGIN_X;
    headerLogos.forEach((lg, i) => {
      const s = Math.min(MAX_H / lg.h, MAX_W / lg.w);
      const w = lg.w * s;
      const h = lg.h * s;
      const lx = rightEdge - w;
      const ly = centerY - h / 2;
      pdf.addImage(lg.dataUrl, 'PNG', lx, ly, w, h);
      headerBottom = Math.max(headerBottom, ly + h);
      const gap = both && i === 0 ? X_GAP : LOGO_GAP;
      if (both && i === 0) {
        // Collaboration "×", drawn as two crossed strokes centered in the gap.
        const xc = lx - gap / 2;
        const r = 1.6;
        pdf.setDrawColor(...COLOR_MUTED);
        pdf.setLineWidth(0.5);
        pdf.line(xc - r, centerY - r, xc + r, centerY + r);
        pdf.line(xc - r, centerY + r, xc + r, centerY - r);
      }
      rightEdge = lx - gap;
    });
  }

  y = headerBottom + 4;
  y = drawDivider(pdf, y);

  // --- Campaign meta block ----------------------------------------------
  drawSectionTitle(pdf, 'Campaign', MARGIN_X, y);
  y += 5;
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(13);
  pdf.setTextColor(...COLOR_TEXT);
  const campLines = pdf.splitTextToSize(campaignName, CONTENT_W);
  pdf.text(campLines, MARGIN_X, y);
  y += campLines.length * 5.5;

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(10);
  pdf.setTextColor(...COLOR_MUTED);
  const advertiserName = row.business_info?.businessName?.trim();
  if (advertiserName) {
    pdf.text(advertiserName, MARGIN_X, y);
    y += 4.5;
  }
  pdf.text(`${formatDate(startDate)} – ${formatDate(endDate)}`, MARGIN_X, y);
  y += 6;

  // --- Summary stats: total / daily avg / goal % ------------------------
  y = drawDivider(pdf, y);
  drawSectionTitle(pdf, 'Summary', MARGIN_X, y);
  y += 5;

  // Four cells when we have a completion rate, otherwise the original three.
  const hasCompletion = completionRate != null;
  const statCols = hasCompletion ? 4 : 3;
  const statCellW = CONTENT_W / statCols;
  let sc = 0;
  drawStatCell(pdf, MARGIN_X + statCellW * sc++, y, 'Total plays', totalDelivered.toLocaleString('en-US'));
  drawStatCell(pdf, MARGIN_X + statCellW * sc++, y, 'Daily avg', dailyAvg.toLocaleString('en-US'));
  if (hasCompletion) {
    drawStatCell(pdf, MARGIN_X + statCellW * sc++, y, 'Completion', `${completionRate}%`);
  }
  drawStatCell(pdf, MARGIN_X + statCellW * sc++, y, 'Goal', goalPct == null ? '—' : `${goalPct}%`);
  y += 12;

  // --- Delivery bar chart (vector primitives) — auto-bucketed to day / week /
  // month at the same granularity the on-page chart defaults to.
  if (daily.length > 0) {
    const trendDaily = daily.map((d) => ({ date: d.date, plays: d.plays, completes: 0 }));
    const trendView = pickTrendView(trendDaily);
    const deliveryMultiYear = new Set(daily.map((d) => d.date.slice(0, 4))).size > 1;
    const trendBuckets = bucketTrend(trendDaily, trendView, deliveryMultiYear);
    y = beginSection(pdf, y, `${viewAdverbial(trendView)} delivery`, 46, () => drawRunningHeader(pdf, config, campaignName));
    y = drawBarChart(pdf, trendBuckets, y, barColor);
    y += 4;
  }

  // --- Daypart (timezone-aware windows) ---------------------------------
  if (daypart && daypart.windows.some((w) => w.impressions > 0)) {
    y = beginSection(pdf, y, `Daypart (${daypart.timezoneLabel})`, 46, () => drawRunningHeader(pdf, config, campaignName));
    y = drawDaypart(pdf, daypart.windows, y, barColor);
    y += 4;
  }

  // --- Geographic delivery: top states + choropleth + ranked tables -----
  if (geo && (geo.byState.length > 0 || geo.byDma.length > 0 || geo.byZip.length > 0)) {
    y += 5; // extra separation above this major section's title (it leads the map)
    const hasMap = !!(geo.zipBoundaries && geo.zipBoundaries.length > 0);
    // Keep the "Geographic delivery" title with its first block — the Top states
    // table when present, otherwise the map / first ranked table — so the title
    // never lands alone at a page bottom.
    const firstBlock = geo.byState.length > 0 ? 30 : hasMap ? 96 : 30;
    y = beginSection(pdf, y, 'Geographic delivery', firstBlock, () => drawRunningHeader(pdf, config, campaignName));

    // Top states first — kept ahead of the (large) map so the states ranking
    // leads the section even when the map flows to the next page.
    if (geo.byState.length > 0) {
      y = beginSection(pdf, y, 'Top states', 26, () => drawRunningHeader(pdf, config, campaignName));
      y = drawGeoTable(pdf, 'State', geo.byState, (c) => ABBR_TO_NAME[c] || c, y, () =>
        drawRunningHeader(pdf, config, campaignName), barColor,
      );
      y += 4;
    }

    // Vector ZIP choropleth ("heatmap"), framed on the main delivery area, when we
    // have the boundaries. It's a ~92mm atomic block, so reserve it whole — it
    // moves to the next page rather than splitting across the page boundary.
    if (geo.zipBoundaries && geo.zipBoundaries.length > 0) {
      const basemap = geo.zipBasemapUrl ? await loadLogoImage(geo.zipBasemapUrl, 1400) : null;
      y = ensurePage(pdf, y, 96, { drawHeader: () => drawRunningHeader(pdf, config, campaignName) });
      y = drawZipChoropleth(pdf, geo.zipBoundaries, basemap, rampRgb, y);
      y += 4;
    }

    if (geo.byDma.length > 0) {
      y = beginSection(pdf, y, 'Top markets (DMA)', 26, () => drawRunningHeader(pdf, config, campaignName));
      y = drawGeoTable(pdf, 'Market', geo.byDma, (c) => DMA_NAME_BY_CODE[c] || c, y, () =>
        drawRunningHeader(pdf, config, campaignName), barColor,
      );
      y += 4;
    }
    if (geo.byZip.length > 0) {
      // Fold the sub-100-impression tail into a single "Other" row (matching the
      // on-page panel), then rank everything together and show the top 20.
      const zipRows = buildZipRankedRows(geo.byZip);
      y = beginSection(pdf, y, zipRows.length > 20 ? 'Top 20 ZIP codes' : 'ZIP codes', 26, () => drawRunningHeader(pdf, config, campaignName));
      y = drawRankedTable(pdf, 'ZIP', zipRows, y, () => drawRunningHeader(pdf, config, campaignName), barColor, 20);
      y += 4;
    }
  }

  // --- By creative (multi-creative campaigns only) ----------------------
  if (creatives && creatives.length > 0) {
    y = beginSection(pdf, y, 'By commercial', 26, () => drawRunningHeader(pdf, config, campaignName));
    y = drawCreativeTable(pdf, creatives, y, () => drawRunningHeader(pdf, config, campaignName), barColor);
    y += 4;
  }

  // --- Monthly breakdown table ------------------------------------------
  if (monthly.length > 0) {
    y = beginSection(pdf, y, 'Monthly breakdown', 26, () => drawRunningHeader(pdf, config, campaignName));
    y = drawMonthlyTable(pdf, monthly, totalDelivered, y, () =>
      drawRunningHeader(pdf, config, campaignName), barColor,
    );
    y += 4;
  }

  // --- Footer (after all content is laid out): support (left) · page number
  // (right) · "Powered by CTVBuyer" wordmark (center), on every page — mirroring
  // the "Powered by" branding in the on-page header.
  const total = pdf.getNumberOfPages();
  const markH = 5.8;
  const markW = markH * (CTVBUYER_WORDMARK_W / CTVBUYER_WORDMARK_H);
  const POWERED_LABEL = 'POWERED BY';
  const LABEL_CHARSPACE = 0.4;
  const LABEL_LOGO_GAP = 2;
  for (let i = 1; i <= total; i++) {
    pdf.setPage(i);
    const footY = PAGE_H - 8;

    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8);
    pdf.setTextColor(...COLOR_MUTED);
    pdf.text(`Page ${i} of ${total}`, PAGE_W - MARGIN_X, footY, { align: 'right' });

    // Centered "POWERED BY" + the CTVBuyer wordmark, vertically aligned on the
    // footer line. getTextWidth excludes charSpace, so add it back for centering.
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(7.5);
    pdf.setTextColor(...COLOR_MUTED);
    const labelW = pdf.getTextWidth(POWERED_LABEL) + LABEL_CHARSPACE * (POWERED_LABEL.length - 1);
    const groupW = labelW + LABEL_LOGO_GAP + markW;
    const startX = (PAGE_W - groupW) / 2;
    pdf.text(POWERED_LABEL, startX, footY, { charSpace: LABEL_CHARSPACE });
    pdf.addImage(CTVBUYER_WORDMARK_PNG, 'PNG', startX + labelW + LABEL_LOGO_GAP, footY - markH + 0.6, markW, markH);
  }

  pdf.save(filename);
}

// Load a logo URL into a PNG data URL (+ pixel dims for aspect) via an off-DOM
// canvas. Downscales to ≤600px so the embedded image stays small. Resolves null
// on any failure — a cross-origin logo without CORS taints the canvas, an
// unreachable/unsupported URL errors; either way the header drops the logo.
async function loadLogoImage(url: string, maxDim = 600): Promise<{ dataUrl: string; w: number; h: number } | null> {
  return new Promise((resolve) => {
    try {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        try {
          const scale = Math.min(1, maxDim / Math.max(img.naturalWidth, img.naturalHeight || 1));
          const w = Math.max(1, Math.round(img.naturalWidth * scale));
          const h = Math.max(1, Math.round(img.naturalHeight * scale));
          const canvas = document.createElement('canvas');
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext('2d');
          if (!ctx) return resolve(null);
          ctx.drawImage(img, 0, 0, w, h);
          resolve({ dataUrl: canvas.toDataURL('image/png'), w, h });
        } catch {
          resolve(null);
        }
      };
      img.onerror = () => resolve(null);
      img.src = url;
    } catch {
      resolve(null);
    }
  });
}

// --- Drawing helpers --------------------------------------------------------

function drawStatCell(pdf: jsPDF, x: number, y: number, label: string, value: string): void {
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(8);
  pdf.setTextColor(...COLOR_MUTED);
  pdf.text(label.toUpperCase(), x, y, { charSpace: 0.3 });

  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(14);
  pdf.setTextColor(...COLOR_TEXT);
  pdf.text(value, x, y + 6);
}

function drawBarChart(
  pdf: jsPDF,
  buckets: Array<{ label: string; plays: number }>,
  y: number,
  barColor: readonly [number, number, number],
): number {
  const CHART_H = 38;
  const GAP = 0.6;
  const Y_AXIS_W = 12; // left gutter for the y-axis labels
  const plotX0 = MARGIN_X + Y_AXIS_W;
  const plotW = CONTENT_W - Y_AXIS_W;
  const colW = (plotW - GAP * (buckets.length - 1)) / buckets.length;

  // Dynamic floored y-range (matches the on-page chart) so steady-high delivery
  // still shows variation.
  const playsVals = buckets.map((b) => b.plays);
  const { min, max, ticks } = niceAxisRange(Math.min(...playsVals), Math.max(1, ...playsVals));
  const span = max - min || 1;
  const yOf = (v: number) => y + (1 - (v - min) / span) * CHART_H;

  // Y-axis gridlines + tick labels (drawn under the bars).
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(8);
  for (const t of ticks) {
    const ty = yOf(t);
    pdf.setDrawColor(...COLOR_LINE);
    pdf.setLineWidth(t === min ? 0.2 : 0.12);
    pdf.line(plotX0, ty, plotX0 + plotW, ty);
    pdf.setTextColor(...COLOR_MUTED);
    pdf.text(fmtAxis(t), plotX0 - 1.5, ty + 1, { align: 'right' });
  }

  // Bars (from the floor).
  pdf.setFillColor(barColor[0], barColor[1], barColor[2]);
  for (let i = 0; i < buckets.length; i++) {
    const h = Math.max(0.5, ((buckets[i].plays - min) / span) * CHART_H);
    const x = plotX0 + i * (colW + GAP);
    pdf.rect(x, y + CHART_H - h, colW, h, 'F');
  }

  // Start / mid / end labels (the buckets carry their own formatted labels).
  pdf.setTextColor(...COLOR_MUTED);
  pdf.text(buckets[0].label, plotX0, y + CHART_H + 4);
  if (buckets.length > 2) {
    pdf.text(buckets[Math.floor(buckets.length / 2)].label, plotX0 + plotW / 2, y + CHART_H + 4, { align: 'center' });
  }
  pdf.text(buckets[buckets.length - 1].label, plotX0 + plotW, y + CHART_H + 4, { align: 'right' });

  return y + CHART_H + 8;
}

// Daypart: one horizontal bar per daypart window — label · bar · plays · compl%.
function drawDaypart(
  pdf: jsPDF,
  windows: Array<{ label: string; hours: string; impressions: number; completes: number }>,
  y: number,
  barColor: readonly [number, number, number],
): number {
  const ROW_H = 8; // room for the daypart name + its time range beneath it
  const LABEL_W = 34; // fits the named dayparts (e.g. "Early Morning")
  const PLAYS_W = 26;
  const PCT_W = 16;
  const GAP = 6;
  const barW = CONTENT_W - LABEL_W - PLAYS_W - PCT_W - GAP;
  // Bars are each window's share of total plays (sum to 100%), so the full bar
  // track represents all plays — matching the geographic section's bars.
  const total = Math.max(1, windows.reduce((s, w) => s + w.impressions, 0));

  // Column labels (matching the other sections' header rows): Window · Share of
  // plays · Plays · Compl., aligned to the same columns the rows below use.
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(8);
  pdf.setTextColor(...COLOR_MUTED);
  pdf.text('WINDOW', MARGIN_X, y, { charSpace: 0.3 });
  pdf.text('SHARE OF PLAYS', MARGIN_X + LABEL_W, y, { charSpace: 0.3 });
  pdf.text('PLAYS', MARGIN_X + LABEL_W + barW + PLAYS_W, y, { align: 'right', charSpace: 0.3 });
  pdf.text('COMPL.', MARGIN_X + CONTENT_W, y, { align: 'right', charSpace: 0.3 });
  pdf.setDrawColor(...COLOR_LINE);
  pdf.setLineWidth(0.2);
  pdf.line(MARGIN_X, y + 1.5, MARGIN_X + CONTENT_W, y + 1.5);
  y += 5;

  for (const w of windows) {
    const rate = w.impressions > 0 ? Math.min(100, Math.round((w.completes / w.impressions) * 100)) : 0;
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(9);
    pdf.setTextColor(...COLOR_TEXT);
    pdf.text(w.label, MARGIN_X, y + 3);
    // Time range under the daypart name.
    pdf.setFontSize(8.5);
    pdf.setTextColor(...COLOR_MUTED);
    pdf.text(w.hours, MARGIN_X, y + 7);

    const barX = MARGIN_X + LABEL_W;
    pdf.setFillColor(...COLOR_LINE);
    pdf.rect(barX, y, barW, 3.4, 'F');
    pdf.setFillColor(barColor[0], barColor[1], barColor[2]);
    if (w.impressions > 0) pdf.rect(barX, y, Math.max(0.6, (w.impressions / total) * barW), 3.4, 'F');

    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(9);
    pdf.setTextColor(...COLOR_TEXT);
    pdf.text(w.impressions.toLocaleString('en-US'), barX + barW + PLAYS_W, y + 3, { align: 'right' });

    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8);
    pdf.setTextColor(...COLOR_MUTED);
    pdf.text(`${rate}%`, MARGIN_X + CONTENT_W, y + 3, { align: 'right' });
    y += ROW_H;
  }
  return y;
}

function hexToRgbLocal(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}

const lerp = (a: number, b: number, t: number): number => Math.round(a + (b - a) * t);

// Two-segment heat interpolation low→mid→high, identical to the on-page
// choropleth's heatColor so the PDF map reads the same. t = plays / max.
function heatColor3(
  t: number,
  low: [number, number, number],
  mid: [number, number, number],
  high: [number, number, number],
): [number, number, number] {
  const c = Math.max(0, Math.min(1, t || 0));
  if (c <= 0.5) {
    const k = c / 0.5;
    return [lerp(low[0], mid[0], k), lerp(low[1], mid[1], k), lerp(low[2], mid[2], k)];
  }
  const k = (c - 0.5) / 0.5;
  return [lerp(mid[0], high[0], k), lerp(mid[1], high[1], k), lerp(mid[2], high[2], k)];
}

// Outer rings (as [lng,lat][]) of a Polygon / MultiPolygon geometry.
function outerRingsOf(geom: GeoJSON.Geometry | null): number[][][] {
  if (!geom) return [];
  if (geom.type === 'Polygon') return [geom.coordinates[0] as number[][]];
  if (geom.type === 'MultiPolygon') return (geom.coordinates as number[][][][]).map((p) => p[0] as number[][]);
  return [];
}

// Area-weighted centroid (shoelace) + area for EACH polygon part of a geometry.
// A multi-part ZIP (e.g. 53711, two separate areas) gets one entry per part so
// each distinct area can be labeled — the census centroid can also sit off the
// visual mass, so we derive each center from its boundary.
function polygonLabelPoints(geom: GeoJSON.Geometry | null): Array<{ lng: number; lat: number; area: number }> {
  const out: Array<{ lng: number; lat: number; area: number }> = [];
  for (const ring of outerRingsOf(geom)) {
    if (ring.length < 3) continue;
    let a = 0;
    let cx = 0;
    let cy = 0;
    const n = ring.length;
    for (let i = 0; i < n; i++) {
      const [x0, y0] = ring[i] as [number, number];
      const [x1, y1] = ring[(i + 1) % n] as [number, number];
      const cross = x0 * y1 - x1 * y0;
      a += cross;
      cx += (x0 + x1) * cross;
      cy += (y0 + y1) * cross;
    }
    a *= 0.5;
    if (Math.abs(a) < 1e-12) continue;
    out.push({ lng: cx / (6 * a), lat: cy / (6 * a), area: Math.abs(a) });
  }
  return out;
}

interface ZipBoundaryDatum {
  zcta: string;
  latitude: number;
  longitude: number;
  boundary: GeoJSON.Geometry | null;
  impressions: number;
}

// Set a layer-wide alpha (jsPDF GState); no-op if unavailable.
function setLayerOpacity(pdf: jsPDF, opacity: number): void {
  try {
    const j = pdf as unknown as { GState: new (o: { opacity: number }) => unknown; setGState: (g: unknown) => void };
    j.setGState(new j.GState({ opacity }));
  } catch {
    /* GState unsupported — fills render opaque */
  }
}

// Vector ZIP choropleth: delivered ZIP boundaries colored by plays, drawn over a
// Mapbox basemap (street context) when available, with the ZIP code labeled
// inside each. Framed on the main delivery area (computeZipMapFrame); ZIPs whose
// centroid falls outside the frame are dropped (they'd land off the map box).
function drawZipChoropleth(
  pdf: jsPDF,
  zips: ZipBoundaryDatum[],
  basemap: { dataUrl: string; w: number; h: number } | null,
  ramp: { low: [number, number, number]; mid: [number, number, number]; high: [number, number, number] },
  y: number,
): number {
  const withGeo = zips.filter((z) => z.boundary);
  if (withGeo.length === 0) return y;
  const frame = computeZipMapFrame(withGeo);
  if (!frame) return y;
  const { minLng, maxLng, minLat, maxLat } = frame;

  // Projected frame (Web Mercator, both axes in radian units).
  const fx0 = (minLng * Math.PI) / 180;
  const fx1 = (maxLng * Math.PI) / 180;
  const fy1 = mercatorY(maxLat);
  const spanX = fx1 - fx0;
  const spanY = fy1 - mercatorY(minLat);
  if (spanX <= 0 || spanY <= 0) return y;

  const MAP_MAX_H = 92;
  let scale = CONTENT_W / spanX;
  if (spanY * scale > MAP_MAX_H) scale = MAP_MAX_H / spanY;
  const mapW = spanX * scale;
  const mapH = spanY * scale;
  const originX = MARGIN_X + (CONTENT_W - mapW) / 2;
  const peak = Math.max(1, ...withGeo.map((z) => z.impressions));

  // Basemap first (aligned: same frame, matching aspect → no distortion).
  if (basemap) pdf.addImage(basemap.dataUrl, 'PNG', originX, y, mapW, mapH);

  // Clamp every vertex to the map box so a ZIP whose polygon pokes past the frame
  // edge doesn't draw outside it (e.g. off the top into the header).
  const toPage = (lng: number, lat: number): [number, number] => {
    const px = originX + ((lng * Math.PI) / 180 - fx0) * scale;
    const py = y + (fy1 - mercatorY(lat)) * scale; // invert: north = up
    return [Math.max(originX, Math.min(originX + mapW, px)), Math.max(y, Math.min(y + mapH, py))];
  };

  const drawn = withGeo.filter(
    (z) => z.longitude >= minLng && z.longitude <= maxLng && z.latitude >= minLat && z.latitude <= maxLat,
  );

  // Polygons: semi-transparent fills so the basemap shows through.
  setLayerOpacity(pdf, 0.62);
  pdf.setLineWidth(0.2);
  pdf.setDrawColor(255, 255, 255);
  for (const z of drawn) {
    const [r, g2, b] = heatColor3(z.impressions / peak, ramp.low, ramp.mid, ramp.high);
    pdf.setFillColor(r, g2, b);
    for (const ring of outerRingsOf(z.boundary)) {
      if (ring.length < 2) continue;
      const start = toPage(ring[0][0], ring[0][1]);
      const deltas: number[][] = [];
      let prev = start;
      for (let i = 1; i < ring.length; i++) {
        const cur = toPage(ring[i][0], ring[i][1]);
        deltas.push([cur[0] - prev[0], cur[1] - prev[1]]);
        prev = cur;
      }
      pdf.lines(deltas, start[0], start[1], [1, 1], 'FD', true);
    }
  }
  setLayerOpacity(pdf, 1);

  // Thin border around the map box.
  pdf.setDrawColor(...COLOR_LINE);
  pdf.setLineWidth(0.3);
  pdf.rect(originX, y, mapW, mapH, 'S');

  // ZIP code labels centered in each drawn ZIP — white on the darker (high-play)
  // fills, slate elsewhere.
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(6);
  for (const z of drawn) {
    if (z.impressions / peak > 0.5) pdf.setTextColor(255, 255, 255);
    else pdf.setTextColor(...COLOR_TEXT);
    // Label each significant polygon part at its area-weighted centroid, so a
    // multi-area ZIP is labeled in each area (skip tiny slivers / off-frame parts).
    const parts = polygonLabelPoints(z.boundary);
    const maxArea = parts.length ? Math.max(...parts.map((p) => p.area)) : 0;
    const labelled = parts.filter((p) => p.area >= maxArea * 0.15);
    const points = labelled.length ? labelled : [{ lng: z.longitude, lat: z.latitude, area: 1 }];
    for (const p of points) {
      if (p.lng < minLng || p.lng > maxLng || p.lat < minLat || p.lat > maxLat) continue;
      const [cx, cy] = toPage(p.lng, p.lat);
      pdf.text(z.zcta, cx, cy, { align: 'center', baseline: 'middle' });
    }
  }

  return y + mapH;
}

// In-cell "Share of plays" bar: a light track with an accent fill ∝ the share %,
// plus the right-aligned % label. Mirrors the on-page by-creative bar so the PDF
// tables read the same. `share` is the percent (0–100, may be fractional) — a
// non-zero share under 0.5% reads "<1%" rather than rounding down to "0%".
function drawShareBar(
  pdf: jsPDF,
  cellX: number,
  cellW: number,
  share: number,
  y: number,
  fill: readonly [number, number, number],
): void {
  const TEXT_W = 8.5; // reserve room for a right-aligned "100%"
  const GAP = 1.5;
  const trackX = cellX + 1;
  const trackW = Math.max(4, cellW - 1 - TEXT_W - GAP);
  const H = 1.6;
  const top = y - 2.2; // sit the bar over the digits' x-height
  // Track.
  pdf.setFillColor(...COLOR_LINE);
  pdf.rect(trackX, top, trackW, H, 'F');
  // Fill (floor a sliver for any non-zero share so small shares still register).
  const pct = Math.max(0, Math.min(100, share));
  const w = pct > 0 ? Math.max(0.5, (pct / 100) * trackW) : 0;
  if (w > 0) {
    pdf.setFillColor(fill[0], fill[1], fill[2]);
    pdf.rect(trackX, top, w, H, 'F');
  }
  // % label, right-aligned to the cell edge (same spot the text used to sit).
  // Never collapse a real (>0) share to "0%" — show "<1%" instead.
  const label = pct <= 0 ? '0%' : pct < 1 ? '<1%' : `${Math.round(pct)}%`;
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(10);
  pdf.setTextColor(...COLOR_TEXT);
  pdf.text(label, cellX + cellW - 1, y, { align: 'right' });
}

// Geographic / creative ranked table: Area · Plays · Share · Compl. Caps at the
// top `limit` rows (default 10) and notes the remainder so long-tail lists stay
// readable.
function drawRankedTable(
  pdf: jsPDF,
  firstHeader: string,
  rows: Array<{ label: string; impressions: number; completes: number }>,
  y: number,
  drawHeader: () => number,
  fill: readonly [number, number, number],
  limit = 10,
): number {
  const ROW_H = 5.5;
  const COL_W = [86, 32, 28, 28];
  const SHARE_X = MARGIN_X + COL_W[0] + COL_W[1];
  const headers = [firstHeader, 'Plays', 'Share', 'Compl.'];
  const aligns: Array<'left' | 'right'> = ['left', 'right', 'right', 'right'];
  const denom = rows.reduce((a, r) => a + r.impressions, 0) || 1;
  const top = rows.slice(0, limit);

  y = drawTableHeaderRow(pdf, headers, COL_W, aligns, y);
  for (const r of top) {
    if (y + ROW_H > PAGE_USABLE_BOTTOM) {
      pdf.addPage();
      y = drawHeader();
      y = drawTableHeaderRow(pdf, headers, COL_W, aligns, y);
    }
    const share = (r.impressions / denom) * 100; // raw; drawShareBar rounds + handles <1%
    const rate = r.impressions > 0 ? Math.min(100, Math.round((r.completes / r.impressions) * 100)) : 0;
    const label = r.label.length > 46 ? r.label.slice(0, 44) + '…' : r.label;
    // Share cell is drawn as a bar (below); leave it blank in the text row.
    drawTableRow(
      pdf,
      [label, r.impressions.toLocaleString('en-US'), '', `${rate}%`],
      COL_W,
      aligns,
      y,
    );
    drawShareBar(pdf, SHARE_X, COL_W[2], share, y, fill);
    y += ROW_H;
  }
  if (rows.length > top.length) {
    pdf.setFont('helvetica', 'italic');
    pdf.setFontSize(8);
    pdf.setTextColor(...COLOR_MUTED);
    pdf.text(`+${rows.length - top.length} more`, MARGIN_X, y + 2);
    y += 5;
  }
  return y;
}

// ZIP table rows: list each ZIP with ≥100 impressions individually, fold the
// sub-100 tail into one "Other" aggregate (matching the on-page panel), then
// rank everything by plays. "Other" is ranked by its total, so it shows up
// among the top 20 only if it earns the spot.
const ZIP_OTHER_MIN = 100;
function buildZipRankedRows(
  byZip: GeoDatum[],
): Array<{ label: string; impressions: number; completes: number }> {
  const rows = byZip
    .filter((r) => r.impressions >= ZIP_OTHER_MIN)
    .map((r) => ({ label: r.code, impressions: r.impressions, completes: r.completes }));
  const tail = byZip.filter((r) => r.impressions < ZIP_OTHER_MIN);
  if (tail.length > 0) {
    rows.push({
      label: 'Other',
      impressions: tail.reduce((a, r) => a + r.impressions, 0),
      completes: tail.reduce((a, r) => a + r.completes, 0),
    });
  }
  return rows.sort((a, b) => b.impressions - a.impressions);
}

function drawGeoTable(
  pdf: jsPDF,
  firstHeader: string,
  rows: GeoDatum[],
  labelOf: (code: string) => string,
  y: number,
  drawHeader: () => number,
  fill: readonly [number, number, number],
): number {
  return drawRankedTable(
    pdf,
    firstHeader,
    rows.map((r) => ({ label: r.name || labelOf(r.code), impressions: r.impressions, completes: r.completes })),
    y,
    drawHeader,
    fill,
  );
}

function drawCreativeTable(
  pdf: jsPDF,
  creatives: Array<{ name: string; impressions: number; completes: number }>,
  y: number,
  drawHeader: () => number,
  fill: readonly [number, number, number],
): number {
  return drawRankedTable(
    pdf,
    'Commercial',
    creatives.map((c) => ({ label: c.name, impressions: c.impressions, completes: c.completes })),
    y,
    drawHeader,
    fill,
  );
}

// Monthly table: 5 columns — Month · Days · Plays · Daily avg · Share.
function drawMonthlyTable(
  pdf: jsPDF,
  monthly: MonthlyRow[],
  totalDelivered: number,
  y: number,
  drawHeader: () => number,
  fill: readonly [number, number, number],
): number {
  const ROW_H = 6;
  // Column widths (sum = CONTENT_W, 174mm). Month + Days span the same 86mm as
  // the ranked tables' label column, and the last three (Plays/Daily avg/Share)
  // match their [32, 28, 28] data columns — so those three line up vertically.
  const COL_W = [64, 22, 32, 28, 28];
  const SHARE_X = MARGIN_X + COL_W[0] + COL_W[1] + COL_W[2] + COL_W[3];
  const headers = ['Month', 'Days', 'Plays', 'Daily avg', 'Share'];
  const aligns: Array<'left' | 'right'> = ['left', 'right', 'right', 'right', 'right'];

  y = drawTableHeaderRow(pdf, headers, COL_W, aligns, y);

  for (const m of monthly) {
    if (y + ROW_H > PAGE_USABLE_BOTTOM) {
      pdf.addPage();
      y = drawHeader();
      y = drawTableHeaderRow(pdf, headers, COL_W, aligns, y);
    }
    const sharePct = totalDelivered > 0 ? (m.plays / totalDelivered) * 100 : 0; // raw; drawShareBar rounds + handles <1%
    drawTableRow(
      pdf,
      [
        m.label,
        String(m.days),
        m.plays.toLocaleString('en-US'),
        m.dailyAvg.toLocaleString('en-US'),
        '', // share rendered as a bar below
      ],
      COL_W,
      aligns,
      y,
    );
    drawShareBar(pdf, SHARE_X, COL_W[4], sharePct, y, fill);
    y += ROW_H;
  }
  return y;
}

function drawTableHeaderRow(
  pdf: jsPDF,
  headers: string[],
  colW: number[],
  aligns: Array<'left' | 'right'>,
  y: number,
): number {
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(8);
  pdf.setTextColor(...COLOR_MUTED);
  let x = MARGIN_X;
  for (let i = 0; i < headers.length; i++) {
    const align = aligns[i];
    const xText = align === 'right' ? x + colW[i] - 1 : x + 1;
    pdf.text(headers[i].toUpperCase(), xText, y, { align: align === 'right' ? 'right' : 'left', charSpace: 0.3 });
    x += colW[i];
  }
  // Underline
  pdf.setDrawColor(...COLOR_LINE);
  pdf.setLineWidth(0.2);
  pdf.line(MARGIN_X, y + 1.5, MARGIN_X + CONTENT_W, y + 1.5);
  return y + 5;
}

function drawTableRow(
  pdf: jsPDF,
  cells: string[],
  colW: number[],
  aligns: Array<'left' | 'right'>,
  y: number,
): void {
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(10);
  pdf.setTextColor(...COLOR_TEXT);
  let x = MARGIN_X;
  for (let i = 0; i < cells.length; i++) {
    const align = aligns[i];
    const xText = align === 'right' ? x + colW[i] - 1 : x + 1;
    pdf.text(cells[i], xText, y, { align: align === 'right' ? 'right' : 'left' });
    x += colW[i];
  }
}

// Running header drawn at the top of every continuation page (page 2+).
// Returns the y cursor after the header so callers can keep writing.
function drawRunningHeader(pdf: jsPDF, config: PublisherConfig, campaignName: string): number {
  const y = MARGIN_TOP;
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(9);
  pdf.setTextColor(...COLOR_MUTED);
  pdf.text(`${config.name} · Delivery Report`, MARGIN_X, y);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(...COLOR_TEXT);
  const truncName = campaignName.length > 60 ? campaignName.slice(0, 58) + '…' : campaignName;
  pdf.text(truncName, PAGE_W - MARGIN_X, y, { align: 'right' });
  pdf.setDrawColor(...COLOR_LINE);
  pdf.setLineWidth(0.2);
  pdf.line(MARGIN_X, y + 2, MARGIN_X + CONTENT_W, y + 2);
  return y + 6;
}

function ensurePage(
  pdf: jsPDF,
  y: number,
  neededHeight: number,
  opts?: { drawHeader?: () => number },
): number {
  if (y + neededHeight <= PAGE_USABLE_BOTTOM) return y;
  pdf.addPage();
  return opts?.drawHeader ? opts.drawHeader() : MARGIN_TOP;
}

function drawSectionTitle(pdf: jsPDF, label: string, x: number, y: number): void {
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(7.5);
  pdf.setTextColor(...COLOR_MUTED);
  pdf.text(label.toUpperCase(), x, y, { charSpace: 0.4 });
}

// Draw a section title, but only after reserving room for the title AND its first
// content block together — so a title never lands alone at the bottom of a page
// with its content (e.g. the geographic map) pushed to the next page.
// `firstBlockHeight` is the height of the first thing drawn under the title: a
// chart, the map, or a table's header + first row. Returns y positioned below the
// title, ready for that content.
function beginSection(
  pdf: jsPDF,
  y: number,
  label: string,
  firstBlockHeight: number,
  drawHeader: () => number,
): number {
  y = ensurePage(pdf, y, SECTION_TITLE_GAP + firstBlockHeight, { drawHeader });
  drawSectionTitle(pdf, label, MARGIN_X, y);
  return y + SECTION_TITLE_GAP;
}

function drawDivider(pdf: jsPDF, y: number): number {
  pdf.setDrawColor(...COLOR_LINE);
  pdf.setLineWidth(0.2);
  pdf.line(MARGIN_X, y, MARGIN_X + CONTENT_W, y);
  return y + 4;
}

function safeFilenamePart(s: string): string {
  return s
    .replace(/[^a-z0-9-]+/gi, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60) || 'report';
}

export function deliveryReportFilenameFor(
  row: SelfServiceSubmissionRow,
  config: PublisherConfig,
  // CTV-462: when set (e.g. "May 2026"), the filename narrows to the scope.
  scopeLabel?: string,
): string {
  const publisher = safeFilenamePart(config.name);
  const campaign = safeFilenamePart(row.campaign?.campaignName || row.business_info?.businessName || 'campaign');
  if (scopeLabel) {
    const scope = safeFilenamePart(scopeLabel);
    return `Delivery-Report-${publisher}-${campaign}-${scope}.pdf`;
  }
  return `Delivery-Report-${publisher}-${campaign}.pdf`;
}
